import {
  useRef, useState, useMemo, Ref,
} from 'react';
import useIntersectionObserver from './useIntersectionObserver';

export type InView<T extends HTMLElement> = {
  inView: boolean;
  ref: Ref<T>;
};

type UseInViewProps = IntersectionObserverInit & {
  trackOnce?: boolean;
};

/**
 * If `trackOnce`, it will set `inView` to `true` the first time the `ref` enters view
 * and the value won't change even if the `ref` moves out of view
 */
const useInView = <T extends HTMLElement>({
  threshold,
  trackOnce,
}: UseInViewProps): InView<T> => {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useIntersectionObserver<T>({
    ref,
    callback: (([entry]) => {
      setInView(trackOnce || entry.isIntersecting);
    }),
    threshold,
  });
  return useMemo(() => ({ inView, ref }), [inView]);
};

export default useInView;
